/**
 * LineDiscountEditor (POS-I119)
 *
 * The per-line bargaining discount control of the till cart. The DEFAULT mode is an amount off
 * the line (money), with a toggle to a percentage. The amount is for the whole line (not per unit)
 * and is capped at the line value; the server re-prices and caps as well (computeLineDiscountAmount).
 *
 * When the shop turned on the manager PIN and the discount reaches the manager threshold, Apply
 * asks for the manager's PIN first (ManagerApprovalDialog, the same dialog the order discount
 * uses) and the approving manager is stored with the line as an audit trail, with the reason.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ManagerApprovalDialog } from '@/components/pos/ManagerApprovalDialog';
import { formatCurrency } from '@/lib/utils';
import {
  checkLineDiscountInput,
  computeLineDiscountAmount,
  lineDiscountPercent,
  type LineDiscountType,
} from '@/lib/line-discount';

export interface LineDiscountValue {
  type: LineDiscountType;
  value: number;
  reason?: string;
  approvedById?: string;
}

interface Props {
  itemName: string;
  /** Unit price x quantity, before the discount. */
  grossAmount: number;
  /** The discount already on the line, when editing. */
  current?: { type: LineDiscountType | null; value: number; reason?: string; approvedById?: string };
  /** Manager PIN switched on for the shop, and the percent at which it is asked. */
  managerPinEnabled: boolean;
  managerDiscountThreshold: number;
  onApply: (next: LineDiscountValue) => void;
  onRemove: () => void;
  onCancel: () => void;
}

export function LineDiscountEditor({
  itemName,
  grossAmount,
  current,
  managerPinEnabled,
  managerDiscountThreshold,
  onApply,
  onRemove,
  onCancel,
}: Props) {
  const { t } = useTranslation('pos');
  // Amount is the default mode; a line that already carries a percentage reopens in percentage.
  const [mode, setMode] = useState<LineDiscountType>(current?.type ?? 'FIXED');
  const [input, setInput] = useState(current?.type && current.value ? String(current.value) : '');
  const [reason, setReason] = useState(current?.reason ?? '');
  const [approvalOpen, setApprovalOpen] = useState(false);

  const value = parseFloat(input);
  const check = checkLineDiscountInput(grossAmount, mode, Number.isNaN(value) ? 0 : value);
  const off = check === 'ok' ? computeLineDiscountAmount(grossAmount, mode, value) : 0;
  const pct = lineDiscountPercent(grossAmount, off);
  // An existing approval only carries over when the discount itself is unchanged.
  const unchanged = !!current?.approvedById && current.type === mode && current.value === value;
  const needsApproval = managerPinEnabled && off > 0 && pct >= managerDiscountThreshold && !unchanged;

  const error = useMemo(() => {
    if (input === '' || check === 'ok') return null;
    if (check === 'overLine') return t('lineDiscountErrOverLine', { max: formatCurrency(grossAmount) });
    if (check === 'overPercent') return t('lineDiscountErrOverPercent');
    return t('lineDiscountErrEmpty');
  }, [check, input, grossAmount, t]);

  const finish = (approvedById?: string) => {
    onApply({
      type: mode,
      value,
      reason: reason.trim() || undefined,
      approvedById: approvedById ?? (unchanged ? current?.approvedById : undefined),
    });
  };

  const apply = () => {
    if (check !== 'ok') return;
    if (needsApproval) {
      setApprovalOpen(true);
      return;
    }
    finish();
  };

  const modeButton = (m: LineDiscountType, label: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      aria-pressed={mode === m}
      className={`h-11 min-w-[88px] px-3 text-sm font-medium transition-colors ${
        mode === m
          ? 'bg-primary text-primary-foreground'
          : 'bg-background text-foreground hover:bg-accent/40'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="mt-2 space-y-2 rounded-lg border border-success/30 bg-success/5 p-2.5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label={t('lineDiscountModeLabel')}
          className="inline-flex overflow-hidden rounded-lg border border-border"
        >
          {modeButton('FIXED', t('lineDiscountModeAmount'))}
          {modeButton('PERCENTAGE', t('lineDiscountModePercent'))}
        </div>
        <Input
          autoFocus
          type="number"
          inputMode="decimal"
          min={0}
          step={mode === 'PERCENTAGE' ? '1' : '0.01'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); apply(); }
            if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
          placeholder="0"
          aria-label={mode === 'PERCENTAGE' ? t('lineDiscountPercentInput') : t('lineDiscountAmountInput')}
          aria-invalid={!!error}
          className="h-11 w-28 text-center"
        />
      </div>

      {error ? (
        <p className="text-xs text-destructive-text" role="alert">{error}</p>
      ) : (
        off > 0 && (
          <p className="text-xs text-success-text">
            {t('lineDiscountPreview', {
              off: formatCurrency(off),
              total: formatCurrency(grossAmount - off),
            })}
          </p>
        )
      )}

      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); apply(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        maxLength={200}
        placeholder={t('lineDiscountReasonPlaceholder')}
        className="h-10 text-sm"
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" className="h-11 gap-1.5" onClick={apply} disabled={check !== 'ok'}>
          <Check className="h-4 w-4" /> {t('lineDiscountApply')}
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-11 gap-1.5" onClick={onCancel}>
          <X className="h-4 w-4" /> {t('lineDiscountCancel')}
        </Button>
        {current?.type && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-11 gap-1.5 text-destructive-text"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" /> {t('lineDiscountRemove')}
          </Button>
        )}
      </div>

      <ManagerApprovalDialog
        open={approvalOpen}
        reason={t('lineDiscountApprovalReason', {
          pct: pct.toFixed(0),
          item: itemName,
          threshold: managerDiscountThreshold,
        })}
        onApproved={(manager) => {
          setApprovalOpen(false);
          finish(manager.id);
        }}
        onCancel={() => setApprovalOpen(false)}
      />
    </div>
  );
}
